import React from "react";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { Alert } from "@mui/material";
import { grey } from "@mui/material/colors";
import clsx from "clsx";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
  },
  fullHeight: {
    height: "100%",
  },
  titleWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2),
  },
  description: {
    color: grey[500],
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    marginBottom: theme.spacing(2),
  },
  fetchingSpinner: {
    marginRight: theme.spacing(1),
  },
  rightWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  cardTitle: {
    fontFamily: ["Sailec", "Arial", "sans-serif"].join(","),
    marginBottom: theme.spacing(1)
  }
}));

function CardBase({
  title = null,
  description = null,
  children,
  isLoading = false,
  isFetching = false,
  error = null,
  fullHeight,
  errorComponent: ErrorComponent = null,
  rightComponent = null,
  flat = false,
}: any) {
  const classes = useStyles();

  const cardContent = () => {
    if (isLoading)
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress data-testid="loading-spinner" color="primary" />
        </Box>
      );

    if (error) {
      if (ErrorComponent) return <ErrorComponent />;
      return (
        <Box className={classes.loadingContainer}>
          <Alert variant="outlined" severity="error">
            {error.message}
          </Alert>
        </Box>
      );
    }

    return children;
  };

  return (
    <Paper
      className={clsx({
        [classes.card]: true,
        [classes.fullHeight]: !!fullHeight,
      })}
      variant={flat ? "outlined" : "elevation"}
      elevation={flat ? 0 : 1}
    >
      {(title || rightComponent) && (
        <div className={classes.titleWrapper}>
          {title && <Typography variant="h5" className={classes.cardTitle}>{title}</Typography>}
          <div className={classes.rightWrapper}>
            {!isLoading && isFetching && (
              <CircularProgress
                className={classes.fetchingSpinner}
                data-testid="fetching-spinner"
                size={24}
                color="primary"
              />
            )}
            {rightComponent}
          </div>
        </div>
      )}
      {description && (
        <Typography className={classes.description} variant="subtitle2">
          {description}
        </Typography>
      )}
      {cardContent()}
    </Paper>
  );
}

export default CardBase;
